// useLinkAgentSession — the bridge between Nexilink's V2 chat UI and the generic
// jasit-agent-kit. It wraps the kit's `useChatAgent` (which owns the
// transport-agnostic conversation state machine) and re-derives the exact
// surface LinkAgentModal renders today: a `ThreadEntry[]`, the pause state, the
// suggestion, and the action handlers.
//
// EVERYTHING Nexilink-specific stays here (an overlay on top of the generic
// kit), NOT in the kit:
//   - the rich ThreadEntry shape (cancelled/"Avbruten", tool `response`
//     verdicts, todo dedupe)
//   - persisted-history hydration (prepended; the kit only owns the LIVE turn)
//   - artifact side-effects (forwarded raw via onEvent; the modal keeps full
//     ownership of artifact tabs / preview / manual edits)
//
// The kit needed ZERO changes to host Nexilink — which is the whole point: a
// Wails/desktop build would swap NexilinkAgentTransport for an in-process one
// and reuse the kit identically.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChatAgent } from 'jasit-agent-kit/react';
import type { QueueMode, QueuedMessage } from 'jasit-agent-kit/react';
import type { ChatEntry, ChatEvent, ChatState, ReattachGate, TurnStatus } from 'jasit-agent-kit';
import { NexilinkAgentTransport } from '@/lib/api/nexilinkAgentTransport';
import { linkQueueStore } from '@/lib/linkAgentQueueStore';
import { linkAgentApi, isSessionGoneError } from '@/lib/api/linkAgentApi';
import type { LinkAgentReference, ReattachPending } from '@/lib/api/linkAgentApi';
import type { ThreadEntry } from '../chat/ChatThread';
import { buildAskReplyResponse, isAskUserReply, cleanAskUserResponseForDisplay } from '../chat/ChatThread';

/** Pause shapes — kept identical to what LinkAgentModal's pause UIs expect. */
export interface PendingApproval {
    approvals: Array<{ call_id: string; tool_name: string; args?: unknown }>;
}
export interface PendingAskUser {
    question: { prompt: string; options: string[]; multi: boolean };
}

/** Outgoing attachment in Nexilink's wire casing. */
export interface LinkAgentAttachment {
    name: string;
    mime: string;
    content_b64: string;
}

/** #48 — a captured send, replayed after a wiped-session recovery so the message
 *  that triggered the wipe isn't lost. Mirrors the send() args. */
export interface LinkAgentResendPayload {
    text: string;
    attachments?: LinkAgentAttachment[];
    displayText?: string;
    references?: LinkAgentReference[];
}

export interface UseLinkAgentSessionOptions {
    sessionId: string | null;
    /** Hydrated history (resume) — rendered by Nexilink, prepended to the live turn. */
    history?: ThreadEntry[];
    /** Raw kit events, for the modal's artifact side-effects (focus/expand/delete). */
    onEvent?: (event: ChatEvent) => void;
    /** #48 — the RAW error thrown by a turn's stream (send/respond/answer), BEFORE
     *  the kit stringifies it into an error entry. Lets the provider detect the
     *  wiped-session case (isSessionGoneError) and auto-recover via resume. The
     *  second arg is the just-attempted SEND payload (text/attachments/references),
     *  present ONLY when the failed op was a send — so the provider can re-send it
     *  after recovery (the message that triggered the wipe would otherwise vanish,
     *  since it never persisted). Absent for respond/answer failures. */
    onError?: (err: unknown, lastSend?: LinkAgentResendPayload) => void;
    /** Called once each turn settles (busy → idle) — rail refresh / auto-title. */
    onTurnEnd?: () => void;
    /** How sends are handled while Link is busy. Default 'off' (block). */
    queue?: QueueMode;
}

export interface UseLinkAgentSession {
    thread: ThreadEntry[];
    isStreaming: boolean;
    pendingApproval: PendingApproval | null;
    pendingAskUser: PendingAskUser | null;
    suggestion: string;
    send: (text: string, attachments?: LinkAgentAttachment[], displayText?: string, references?: LinkAgentReference[]) => void;
    /** approve=false + feedback rides as deny_reason (the unified /respond path). */
    approve: (approve: boolean, feedback?: string) => void;
    answer: (answers: string[]) => void;
    /** #111 B1 — resolve an open ask_user with a free-form REPLY instead of an
     *  answer. The engine records the question as unanswered and hands the model
     *  the text on its own terms, so it must NOT be used for a custom answer —
     *  that is still `answer`. No-ops when the transport can't carry one. */
    answerWithText: (text: string) => void;
    /** True when the transport wires answerWithText — gate the composer on this. */
    canAnswerWithText: boolean;
    stop: () => void;
    /** #300 — user-triggered non-destructive compaction ("Komprimera samtalet").
     *  No-ops if the turn is busy or the transport lacks compact support. */
    compact: () => void;
    // ── Reload-reattach primitives (jasit-agent-kit Proposal 29) ─────────────
    // The run-state DECISION (running→replay / paused→gate / idle→none) + the
    // post-commit timing now live in the kit's useChatSession integrated reattach;
    // this hook just supplies the three Nexilink-shaped primitives it dispatches to.
    /** Query the parked/running turn's state as the kit's normalised TurnStatus.
     *  Folds Nexilink's two-endpoint shape (status + the JSON /reattach that carries
     *  the pending gate) into one value. */
    getTurnStatus: () => Promise<TurnStatus>;
    /** Replay + tail a still-RUNNING backgrounded turn (kit reattachRunning). */
    reattachRunning: (fromOffset?: number) => void;
    /** #298 — re-present a PAUSED turn's gate as the app overlay so the user can
     *  answer it live; approve()/answer() then resume with the explicit call_ids. */
    presentReattachGate: (gate: ReattachGate) => void;
    clearSuggestion: () => void;
    /** #26 — re-show the suggestion a resumed chat was left on. The kit has no
     *  setter for its own suggestion state (it restores from per-message `suggested`
     *  metadata, which Nexilink's resume payload does not carry), so the seeded
     *  value is held here and yields to the kit's the moment a live turn produces
     *  one. Before #320 this called `agent.seedSuggestion`, which never existed —
     *  the property was undefined, so resuming a chat that HAD a stored suggestion
     *  threw "is not a function" instead of showing it. */
    seedSuggestion: (text: string) => void;
    /** Messages queued while Link was busy (empty unless queue is enabled). */
    queued: QueuedMessage[];
    dequeue: (index: number) => void;
    clearQueue: () => void;
    /** Clear the kit's live turn (entries/streaming/pending). The kit does NOT
     *  reset when the transport/sessionId changes, so a persistent owner that
     *  reuses ONE useLinkAgentSession across sessions (LinkAgentProvider) calls this on a
     *  session switch before swapping sessionId + history. */
    reset: (state?: ChatState) => void;
}

// A no-op transport for the brief window before a session id exists. The kit
// refs the transport per render, so swapping to the real one is seamless.
const NOOP_TRANSPORT = {
    // eslint-disable-next-line require-yield
    async *send() {},
    async *respond() {},
    async *answer() {},
    async cancel() {},
};

/** Map the kit's committed entries (+ live streaming text) to Nexilink's
 *  ThreadEntry[], applying the Nexilink-only overlays: todo dedupe, tool
 *  verdict `response`, and rendering the kit's cancelled mark as "Avbruten".
 *  Cancelled-turn tracking now lives in the KIT (state.cancelled, persistent
 *  until reload); we only render it. */
function deriveLiveThread(
    state: ChatState,
    // verdict per SOURCE entry index (agent.state.entries). Keyed by index —
    // NOT tool name — so each ask_user / plan_ready keeps its OWN answer when a
    // conversation has many of them (the single-value-per-name map dropped all
    // but the last → "Inget svar registrerat" on every earlier one).
    verdictByIndex: Map<number, string>,
): ThreadEntry[] {
    const out: ThreadEntry[] = [];
    state.entries.forEach((e: ChatEntry, srcIdx: number) => {
        const cancelled = state.cancelled.has(srcIdx);
        switch (e.kind) {
            case 'user':
                out.push({ kind: 'user', text: e.text, cancelled });
                break;
            case 'assistant':
                out.push({ kind: 'assistant', text: e.text, cancelled });
                break;
            case 'error':
                // #48 — a wiped-session error ("session not found (server restart
                // wipes …)") is auto-recovered by the provider (resume-from-
                // transcript); never render its scary line, not even for the brief
                // window before recovery resets the turn. Genuine errors still show.
                if (isSessionGoneError({ message: e.text })) break;
                out.push({ kind: 'error', text: e.text, cancelled });
                break;
            case 'tool': {
                // `todo` carries the whole checklist each call — the latest
                // supersedes; drop any earlier todo entry.
                if (e.name === 'todo') {
                    for (let i = out.length - 1; i >= 0; i--) {
                        const p = out[i];
                        if (p.kind === 'tool' && p.name === 'todo') {
                            out.splice(i, 1);
                            break;
                        }
                    }
                }
                out.push({
                    kind: 'tool',
                    name: e.name,
                    description: e.description,
                    args: e.args,
                    resultLabel: e.resultLabel,
                    result: e.result, // #242 — raw tool result for rich card rendering
                    // Same treatment as hydrated history: detect reply-ness from
                    // the engine-shaped verdict, then clean it for display. Doing
                    // both here is what makes the live and reloaded views render
                    // identically instead of by coincidence.
                    response: cleanAskUserResponseForDisplay(verdictByIndex.get(srcIdx), e.name),
                    responseIsReply: e.name === 'ask_user' && isAskUserReply(verdictByIndex.get(srcIdx)),
                    cancelled,
                });
                break;
            }
            default: {
                // #300 — the kit's non-destructive compaction divider is a
                // { kind:'custom', key:'compacted', data:{summary} } entry.
                // deriveLiveThread otherwise only knows the 4 core kinds, so
                // without this the divider (and its summary) was dropped and the
                // freshly-written summary appeared to vanish. Pass it through as a
                // typed 'compacted' ThreadEntry carrying the summary.
                const anyE = e as unknown as { kind?: string; key?: string; data?: { summary?: string } };
                if (anyE.kind === 'custom' && anyE.key === 'compacted') {
                    out.push({ kind: 'compacted', summary: anyE.data?.summary });
                }
                break;
            }
        }
    });

    // Live streaming text → trailing assistant bubble (uncommitted).
    if (state.streaming) {
        out.push({ kind: 'assistant', text: state.streaming });
    }
    return out;
}

/**
 * Find the latest tool entry named `name` that hasn't been given a verdict yet,
 * and record `verdict` against its index. Returns false when no such entry
 * exists YET (e.g. plan_ready, whose tool_start fires only AFTER approval) —
 * the caller stashes it and a drain effect retries when the entry appears.
 */
function attachVerdict(
    entries: readonly ChatEntry[],
    byIndex: Map<number, string>,
    name: string,
    verdict: string,
): boolean {
    for (let i = entries.length - 1; i >= 0; i--) {
        const e = entries[i];
        if (e.kind === 'tool' && e.name === name && !byIndex.has(i)) {
            byIndex.set(i, verdict);
            return true;
        }
    }
    return false;
}

export function useLinkAgentSession(options: UseLinkAgentSessionOptions): UseLinkAgentSession {
    const { sessionId, history, onEvent, onError, onTurnEnd, queue } = options;

    // #26 seed — see seedSuggestion on the interface. Cleared whenever the session
    // changes, so a resumed chat can never inherit the hint from the last one.
    const [seededSuggestion, setSeededSuggestion] = useState('');
    useEffect(() => { setSeededSuggestion(''); }, [sessionId]);

    const transport = useMemo(
        () => (sessionId ? new NexilinkAgentTransport(sessionId) : NOOP_TRANSPORT),
        [sessionId],
    );

    // Forward raw events + raw stream errors to the provider (artifact
    // side-effects + #48 session-gone recovery live there). Ref-mirrored so the
    // kit callbacks stay stable across renders.
    const onEventRef = useRef(onEvent);
    onEventRef.current = onEvent;
    const onErrorRef = useRef(onError);
    onErrorRef.current = onError;
    // #48 — the last SEND payload, so a wiped-session recovery can replay the
    // message that triggered the wipe (it never persisted, so resume can't restore
    // it — without this it silently vanishes). Set on send; CLEARED on
    // approve/answer/stop so a respond/answer failure never replays a stale send.
    const lastSendRef = useRef<LinkAgentResendPayload | null>(null);
    const agent = useChatAgent(transport, {
        cancelPolicy: 'keep', // keep the partial; we grey it as "Avbruten"
        // Default 'off' here = Nexilink's classic block-while-thinking unless a
        // caller opts in (LinkAgentModal passes a mode). The kit itself defaults to
        // 'batched', so we pin 'off' explicitly.
        queue: queue ?? 'off',
        onEvent: useCallback((e: ChatEvent) => onEventRef.current?.(e), []),
        // #48 — the kit calls this with the RAW thrown error on a stream failure.
        // Pass the last send so the provider can replay it after recovery.
        onError: useCallback((err: unknown) => onErrorRef.current?.(err, lastSendRef.current ?? undefined), []),
    });

    // Nexilink overlays.
    // verdictByIndexRef: source-entry-index → "Du svarade…/Du godkände…" verdict.
    // pendingVerdictsRef: verdicts whose target tool entry hasn't been emitted
    // yet (plan_ready fires its tool_start only after approval) — drained below.
    const verdictByIndexRef = useRef<Map<number, string>>(new Map());
    const pendingVerdictsRef = useRef<Array<{ name: string; verdict: string }>>([]);
    const [verdictVersion, bumpVerdict] = useState(0); // re-derive when verdicts change

    const recordVerdict = (name: string, verdict: string) => {
        if (!attachVerdict(agent.state.entries, verdictByIndexRef.current, name, verdict)) {
            pendingVerdictsRef.current.push({ name, verdict });
        }
        bumpVerdict((v) => v + 1);
    };

    // #298 — a pending pause re-presented by /reattach (the kit's own state is
    // empty on reopen since it only owns the live turn; history is hydrated
    // separately). This app-level overlay stands in for kit pending state until
    // the user answers, at which point approve()/answer() drive the resumed turn
    // with its explicit call_ids and clear the overlay.
    const [reattached, setReattached] = useState<ReattachPending | null>(null);
    // #25 — the answer given to a REATTACH-overlay ask_user. Unlike a live pause,
    // a reattached ask_user's tool entry is NOT in agent.state.entries (it fired
    // before the reload and lives only in the hydrated `history`), so recordVerdict
    // has nothing to attach to. We stash the answer here and reconcile it onto the
    // trailing hydrated-history ask_user row in `thread` — otherwise that row shows
    // "Inget svar registrerat" even though the user answered. Self-correcting: the
    // next resume re-hydrates history WITH the backend-persisted answer (e.response
    // set), so this injection stops applying.
    const [reattachAnswer, setReattachAnswer] = useState<string | null>(null);
    // A new session id means a different conversation — drop any stale overlay/answer.
    useEffect(() => {
        setReattached(null);
        setReattachAnswer(null);
    }, [sessionId]);

    // ── Pending send-queue persistence (B10) ─────────────────────────────────
    // The kit's queue is in-memory, so a reload/restart mid-turn loses it. We
    // persist it per session to localStorage and restore via agent.seedQueue —
    // but the restore is ordered against the turn state (see seedPendingQueue +
    // its call sites) so it never dispatches into a still-running turn.
    const pendingQueueRef = useRef<QueuedMessage[]>([]);
    // Armed = persist may write. Disarmed while a STORED queue is waiting to be
    // restored, so the initial empty reducer can't clobber it before seeding.
    const queueArmedRef = useRef(true);
    // Load the stored queue on a session change; hold it until we can seed it in
    // lockstep with the turn state. This effect is defined BEFORE the persist one
    // so, on a session switch, it disarms persist first.
    useEffect(() => {
        pendingQueueRef.current = linkQueueStore.get(sessionId);
        queueArmedRef.current = pendingQueueRef.current.length === 0;
    }, [sessionId]);
    // Restore the stored queue. Callers MUST order this vs the turn: for a RUNNING
    // or resumed-PAUSED turn, call it AFTER the reattach/resume has set busy (so
    // the kit's dispatch-on-idle effect WAITS instead of firing the queue into the
    // live turn); for an IDLE session, call it directly (it dispatches — the
    // backend turn is already done). Idempotent + once-per-session.
    const seedPendingQueue = useCallback(() => {
        if (queueArmedRef.current) return; // nothing pending / already restored
        const items = pendingQueueRef.current;
        pendingQueueRef.current = [];
        queueArmedRef.current = true; // arm persist
        if (items.length) agent.seedQueue(items);
    }, [agent]);
    // Persist on every change (incl. flush-to-empty), once armed, keyed by session.
    useEffect(() => {
        if (!queueArmedRef.current) return;
        linkQueueStore.save(sessionId, agent.queued);
    }, [sessionId, agent.queued]);

    // onTurnEnd on busy true→false.
    const prevBusyRef = useRef(false);
    const onTurnEndRef = useRef(onTurnEnd);
    onTurnEndRef.current = onTurnEnd;
    useEffect(() => {
        if (prevBusyRef.current && !agent.busy) onTurnEndRef.current?.();
        prevBusyRef.current = agent.busy;
    }, [agent.busy]);

    // Drain stashed verdicts once their tool entries appear (e.g. plan_ready
    // after approval). Runs whenever the entry list grows.
    useEffect(() => {
        if (pendingVerdictsRef.current.length === 0) return;
        const remaining: Array<{ name: string; verdict: string }> = [];
        let changed = false;
        for (const pv of pendingVerdictsRef.current) {
            if (attachVerdict(agent.state.entries, verdictByIndexRef.current, pv.name, pv.verdict)) {
                changed = true;
            } else {
                remaining.push(pv);
            }
        }
        pendingVerdictsRef.current = remaining;
        if (changed) bumpVerdict((v) => v + 1);
    }, [agent.state.entries]);

    const liveThread = useMemo(
        () => deriveLiveThread(agent.state, verdictByIndexRef.current),
        // verdictVersion forces a re-derive when recordVerdict mutates the ref
        // (which isn't a reactive dependency). The cancelled marks live on
        // agent.state (state.cancelled), so [agent.state] already covers cancel.
        [agent.state, verdictVersion],
    );

    // #288 — reconcile hydrated history against the live pending gate.
    // On reopen mid-turn, `history` now holds the FULL live conversation
    // (Session.Messages()): the assistant text written before the pause AND the
    // dangling pending tool_call. The interactive gate for that call is rendered
    // SEPARATELY by the overlay (pendingApproval / pendingAskUser), so its static
    // history row must be dropped — otherwise it shows twice (a ghost "pending"
    // tool row beside the live approval card). Approvals carry call_ids → exact
    // match. ask_user carries no call_id through the reattach overlay, so we drop a
    // single trailing unresolved ask_user row (the pause is always at the tail).
    // Also a general dedup safety-net: nothing that is the active gate survives in
    // history, so counts drifting can never double-render again.
    const thread = useMemo(() => {
        if (!history || !history.length) return liveThread;

        const approval =
            agent.state.pendingApproval ?? (reattached?.kind === 'approval' ? reattached : null);
        const suppressIds = new Set((approval?.approvals ?? []).map((a) => a.call_id));
        const suppressAskUser =
            !!agent.state.pendingAskUser || reattached?.kind === 'ask_user';

        let hist = history;
        if (suppressIds.size || suppressAskUser || reattachAnswer) {
            const lastIdx = history.length - 1;
            // The callback is annotated because TS infers the union of every branch's
            // return (some arrays, some ThreadEntry) and then cannot match flatMap's
            // signature. Saying ThreadEntry[] once is clearer than casting each branch.
            hist = history.flatMap((e, i): ThreadEntry[] => {
                if (e.kind !== 'tool') return [e];
                if (e.callId && suppressIds.has(e.callId)) return [];
                if (e.name === 'ask_user' && !e.response && i === lastIdx) {
                    // While the reattach overlay is showing this gate live, drop the
                    // static history row (it's rendered as the pause UI instead).
                    if (suppressAskUser) return [];
                    // #25 — once answered via the overlay, the row reappears; inject
                    // the answer so it reads "Du svarade: …" not "Inget svar registrerat".
                    if (reattachAnswer) return [{ ...e, response: reattachAnswer }];
                }
                return [e];
            });
        }
        return [...hist, ...liveThread];
    }, [history, liveThread, agent.state.pendingApproval, agent.state.pendingAskUser, reattached, reattachAnswer]);

    const send = useCallback(
        (text: string, attachments?: LinkAgentAttachment[], displayText?: string, references?: LinkAgentReference[]) => {
            const atts = attachments?.length
                ? attachments.map((a) => ({ name: a.name, mime: a.mime, contentBase64: a.content_b64 }))
                : undefined;
            // #240 Wave 5 — stash structured references on the transport for this
            // one turn (the kit's send() doesn't carry them). NOOP_TRANSPORT lacks
            // the method, so guard defensively.
            if (transport instanceof NexilinkAgentTransport) {
                transport.setPendingReferences(references);
            }
            // #48 — capture for a possible post-recovery replay (kept in wire casing
            // so the provider can hand it straight back to send()).
            lastSendRef.current = { text, attachments, displayText, references };
            void agent.send(text, atts, displayText);
        },
        [agent, transport],
    );

    const approve = useCallback(
        (approve: boolean, feedback?: string) => {
            lastSendRef.current = null; // #48 — not a send; don't replay a stale one
            // Prefer the kit's live pending; fall back to the #298 re-attach
            // overlay (kit state is empty on reopen, but the parked turn on the
            // server still holds these call_ids).
            const pending =
                agent.state.pendingApproval ??
                (reattached?.kind === 'approval' ? reattached : null);
            if (!pending) return;
            const trimmed = feedback?.trim() ?? '';
            const verdict = approve ? 'approved' : `denied: ${trimmed || 'avbruten'}`;
            for (const a of pending.approvals) recordVerdict(a.tool_name, verdict);
            setReattached(null); // consume the overlay (harmless if it wasn't the source)
            void agent.respond(
                pending.approvals.map((a) => ({
                    call_id: a.call_id,
                    approve,
                    deny_reason: !approve && trimmed ? trimmed : undefined,
                    tool_name: a.tool_name,
                })),
            );
            seedPendingQueue(); // resume set busy → a restored queue waits + flushes at turn end
        },
        [agent, reattached, seedPendingQueue],
    );

    const answer = useCallback(
        (answers: string[]) => {
            lastSendRef.current = null; // #48 — not a send; don't replay a stale one
            const live = !!agent.state.pendingAskUser;
            if (!live && reattached?.kind !== 'ask_user') return;
            const joined = answers.join(', ');
            if (live) {
                // Live pause — the ask_user tool entry is in agent.state.entries, so
                // the verdict attaches there (deriveLiveThread renders it).
                recordVerdict('ask_user', joined);
            } else {
                // #25 — reattach-overlay answer: the ask_user entry is only in the
                // hydrated history, not agent.state.entries. Reconcile onto that row
                // via reattachAnswer (see `thread`). NOT recordVerdict — a stashed
                // pending 'ask_user' verdict would wrongly cross-attach to the NEXT
                // live ask_user the resumed turn raises.
                setReattachAnswer(joined);
            }
            setReattached(null);
            void agent.answer(answers);
            seedPendingQueue(); // resume set busy → a restored queue waits + flushes at turn end
        },
        [agent, reattached, seedPendingQueue],
    );

    // #111 B1 — the user typed in the composer while a question was open. Same
    // guards as answer(): only valid against a live pause or a re-presented
    // reattach gate. The verdict recorded here is the raw text; the archived
    // renderer distinguishes reply from answer off the engine's persisted
    // wrapper (isAskUserReply), so the two views agree after a reload.
    const answerWithText = useCallback(
        (text: string) => {
            const trimmed = text.trim();
            if (!trimmed) return;
            lastSendRef.current = null; // #48 — not a send; don't replay a stale one
            const live = !!agent.state.pendingAskUser;
            if (!live && reattached?.kind !== 'ask_user') return;
            // Record the ENGINE-SHAPED response, not the bare text. The archived
            // renderer decides reply-vs-answer by parsing that wrapper, so an
            // optimistic bare string would render as "Eget svar" now and flip to
            // "Svarade inte — skrev i stället" after a reload. Same bytes = same
            // rendering, live and rehydrated.
            const optimistic = buildAskReplyResponse(trimmed);
            if (live) {
                recordVerdict('ask_user', optimistic);
            } else {
                setReattachAnswer(optimistic);
            }
            setReattached(null);
            void agent.answerWithText(trimmed);
            seedPendingQueue();
        },
        [agent, reattached, seedPendingQueue],
    );

    // getTurnStatus — normalise Nexilink's server shape into the kit's TurnStatus
    // for the integrated reattach decision (decideReattach). Two-endpoint fold:
    //  • `running` → replay the missed partial turn from offset 0 (the fresh-reload-
    //    mid-turn case history hydration can't cover — the reply isn't snapshotted).
    //  • `waiting` → the JSON /reattach un-detaches server-side AND returns the
    //    pending gate; carry it as a paused TurnStatus so the kit calls presentGate.
    //  • anything else (idle/done/gone) → nothing to reattach (history already has it).
    // The kit guards the double-attach case (isBusy) and the post-commit timing.
    const getTurnStatus = useCallback(async (): Promise<TurnStatus> => {
        if (!sessionId) return { state: 'idle' };
        const st = await linkAgentApi.getTurnStatus(sessionId);
        // running → reattachRunning (onReplay) will restore the queue after it sets
        // busy; waiting → approve/answer will restore it when the gate resumes. Both
        // leave the stored queue pending so it can't dispatch into the live turn.
        if (st.state === 'running') return { state: 'running', nextOffset: 0 };
        if (st.state === 'waiting') {
            const res = await linkAgentApi.reattach(sessionId);
            if (res.reattached && res.pending) {
                const p = res.pending;
                const gate: ReattachGate =
                    p.kind === 'approval'
                        ? { kind: 'approval', request: { approvals: p.approvals } }
                        : { kind: 'ask_user', question: p.question };
                return { state: 'paused', gate };
            }
        }
        // idle → the backend turn is done, so a restored queue can dispatch now.
        seedPendingQueue();
        return { state: 'idle' };
    }, [sessionId, seedPendingQueue]);

    // Replay + tail a still-running backgrounded turn (fired, not awaited — it
    // streams into the reducer like a live send). The kit no-ops on NOOP_TRANSPORT.
    const reattachRunning = useCallback(
        (fromOffset = 0) => {
            void agent.reattachRunning(fromOffset); // drive() sets busy=true synchronously
            seedPendingQueue(); // seeded in the SAME tick → busy batches → queue WAITS, flushes at turn end
        },
        [agent, seedPendingQueue],
    );

    // Re-present a paused gate as the #298 overlay (kit ReattachGate → Nexilink
    // ReattachPending). approve()/answer() consume it and drive the resumed turn.
    const presentReattachGate = useCallback((gate: ReattachGate) => {
        if (gate.kind === 'approval') {
            setReattached({ kind: 'approval', approvals: gate.request.approvals });
            return;
        }
        if (gate.kind === 'ask_user') {
            setReattached({ kind: 'ask_user', question: gate.question });
            return;
        }
        // #320 — ReattachGate has a THIRD variant, ask_user_form. The old code
        // took `: { kind: 'ask_user', question: gate.question }` as the else, so a
        // reattached FORM would have presented an ask_user card whose question was
        // undefined — a blank gate the user cannot answer and cannot dismiss.
        //
        // Link does not emit forms today (that is #111 C), so this is unreachable
        // rather than fixed. Refusing to present it is the honest failure: the turn
        // stays paused server-side and a reload re-presents it, where a blank card
        // would look like the app had lost the question.
        console.warn('[link] reattached ask_user_form gate is not supported yet', gate);
    }, []);

    const stop = useCallback(() => {
        lastSendRef.current = null; // #48 — user cancelled; nothing to replay
        // The kit marks the interrupted turn (state.cancelled, persistent until
        // reload); deriveLiveThread renders those entries greyed as "Avbruten".
        void agent.cancel();
    }, [agent]);

    // #300 — non-destructive compaction. The kit no-ops if the transport has no
    // compact() or a turn is live; guard on busy too so the menu item is inert
    // mid-stream.
    const compact = useCallback(() => {
        if (agent.busy) return;
        void agent.compact();
    }, [agent]);

    const pendingApproval: PendingApproval | null = agent.state.pendingApproval
        ? { approvals: agent.state.pendingApproval.approvals }
        : reattached?.kind === 'approval'
          ? { approvals: reattached.approvals }
          : null;
    const pendingAskUser: PendingAskUser | null = agent.state.pendingAskUser
        ? { question: agent.state.pendingAskUser }
        : reattached?.kind === 'ask_user'
          ? { question: reattached.question }
          : null;

    return {
        thread,
        isStreaming: agent.busy,
        pendingApproval,
        pendingAskUser,
        // A live suggestion always wins: the seed describes the turn the user left
        // on, and the moment a new turn ends that description is out of date.
        suggestion: agent.state.suggestion ?? seededSuggestion,
        send,
        approve,
        answer,
        answerWithText,
        canAnswerWithText: agent.canAnswerWithText,
        stop,
        compact,
        getTurnStatus,
        reattachRunning,
        presentReattachGate,
        clearSuggestion: () => { setSeededSuggestion(''); agent.clearSuggestion(); },
        seedSuggestion: setSeededSuggestion,
        queued: agent.queued,
        dequeue: agent.dequeue,
        clearQueue: agent.clearQueue,
        reset: agent.reset,
    };
}
